/**
 * ReadingCorner — personal greeting + compact reading stats.
 * Replaces the old generic "Reading Progress" dashboard card.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Bookmark, BookOpen, CheckCircle2, Heart, Plus } from 'lucide-react';
import { Button, cx } from '@/components/site/primitives';
import { fadeUp } from '@/lib/motion';
import { BOOKSHELF } from '@/constants/testIds';

const STAT_ITEMS = [
  { key: 'tbr', shelfId: 'shelf_tbr', label: 'TBR', icon: Bookmark, tone: 'text-blue' },
  { key: 'currentlyReading', shelfId: 'shelf_reading', label: 'Reading', icon: BookOpen, tone: 'text-coral' },
  { key: 'finished', shelfId: 'shelf_finished', label: 'Finished', icon: CheckCircle2, tone: 'text-[#6f8f63] dark:text-sage' },
  { key: 'favorites', shelfId: 'shelf_favorites', label: 'Favorites', icon: Heart, tone: 'text-[#c08a3e] dark:text-yellow' },
];

export default function ReadingCorner({ stats, activeShelf, onStatClick, onAddBookClick, userName }) {
  const waiting = stats.tbr;

  return (
    <section className="u-container pt-8 md:pt-12" data-testid={BOOKSHELF.header}>
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <motion.p variants={fadeUp} custom={0} initial="hidden" animate="show" className="u-eyebrow mb-3">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-coral" /> Your personal shelf
          </motion.p>
          <motion.h1 variants={fadeUp} custom={1} initial="hidden" animate="show" className="font-display text-3xl font-semibold tracking-tight text-ink sm:text-4xl">
            {userName ? `${userName}'s Reading Corner` : 'Your Reading Corner'}
          </motion.h1>
          <motion.p variants={fadeUp} custom={2} initial="hidden" animate="show" className="mt-2 text-sm text-subtle md:text-base">
            {waiting > 0
              ? `${waiting} ${waiting === 1 ? 'story is' : 'stories are'} waiting for you.`
              : 'Every book you love, all in one cozy place.'}
          </motion.p>
        </div>

        <motion.div variants={fadeUp} custom={2} initial="hidden" animate="show" className="hidden md:block">
          <Button variant="primary" icon={false} onClick={onAddBookClick} data-testid="bookshelf-add-book-cta">
            <Plus className="h-4 w-4" /> Add Book
          </Button>
        </motion.div>
      </div>

      <motion.div
        variants={fadeUp} custom={3} initial="hidden" animate="show"
        className="mt-7 flex flex-wrap gap-2.5" data-testid={BOOKSHELF.overviewCard} role="group" aria-label="Reading stats"
      >
        {STAT_ITEMS.map(({ key, shelfId, label, icon: Icon, tone }) => {
          const isActive = activeShelf === shelfId;
          return (
            <button
              key={key}
              type="button"
              onClick={() => onStatClick?.(shelfId)}
              className={cx(
                'card-paper flex items-center gap-2.5 rounded-full px-4 py-2.5 text-left transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:shadow-soft',
                isActive && 'border-coral/60 ring-1 ring-coral/30'
              )}
              aria-pressed={isActive}
            >
              <Icon className={cx('h-4 w-4 shrink-0', tone)} />
              <span className="font-display text-base font-semibold leading-none text-ink">{stats[key]}</span>
              <span className="text-xs font-medium leading-none text-subtle">{label}</span>
            </button>
          );
        })}
      </motion.div>
    </section>
  );
}
